// bulkSpareListApiSlice.js
import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { apiSlice } from "../../apiSlice";

/* ------------------ Adapter ------------------ */
const bulkSpareAdapter = createEntityAdapter({
  selectId: (item) => item.request_id,
});

const initialState = bulkSpareAdapter.getInitialState();

/* ------------------ API Slice ------------------ */
export const bulkSpareListSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* -------- LIST -------- */
    getBulkSpareList: builder.query({
      query: ({ seller_id, status } = {}) => {
        let queryString = "/bulk_deals/spares";
        const params = new URLSearchParams();

        if (seller_id) params.append("seller_id", seller_id);
        if (status) params.append("status", status);

        if (params.toString()) {
          queryString += `?${params.toString()}`;
        }

        return queryString;
      },

      transformResponse: (response) =>
        bulkSpareAdapter.setAll(initialState, response.data),

      providesTags: (result) =>
        result
          ? [
              { type: "BulkSpare", id: "LIST" },
              ...result.ids.map((id) => ({ type: "BulkSpare", id })),
            ]
          : [{ type: "BulkSpare", id: "LIST" }],
    }),

    /* -------- GET BY ID -------- */
    getBulkSpareById: builder.query({
      query: (id) => `/bulk_deals/spares/${id}`,

      providesTags: (result, error, id) => [{ type: "BulkSpare", id }],
    }),

    /* -------- UPDATE (PATCH) -------- */
    updateBulkSpareById: builder.mutation({
      query: ({ request_id, data }) => ({
        url: `/bulk_deals/spares/${request_id}`,
        method: "PATCH",
        body: data,
      }),

      invalidatesTags: (result, error, { request_id }) => [
        { type: "BulkSpare", id: request_id },
        { type: "BulkSpare", id: "LIST" },
      ],
    }),

    /* -------- FILE UPLOAD -------- */
    uploadBulkSpareFile: builder.mutation({
      query: (file) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: "/bulk_deals/spares/upload_file",
          method: "POST",
          body: formData,
        };
      },

      invalidatesTags: [{ type: "BulkSpare", id: "LIST" }],
    }),
    
    
  }),
});

/* ------------------ Hooks ------------------ */

export const {
  useGetBulkSpareListQuery,
  useGetBulkSpareByIdQuery,
  useUpdateBulkSpareByIdMutation, // ✅ added
  useUploadBulkSpareFileMutation,
} = bulkSpareListSlice;

/* ------------------ Selectors ------------------ */

const bulkSparesFilter = (state) => state.bulkSparesFilter;

const selectBulkSpareQueryResult = createSelector(
  [bulkSparesFilter, (state) => state],
  (filters, state) => {
    return bulkSpareListSlice.endpoints.getBulkSpareList.select({
      seller_id: filters.seller_id,
      status: filters.status,
    })(state);
  },
);

const selectBulkSpareData = createSelector(
  [selectBulkSpareQueryResult],
  (queryResult) => queryResult?.data ?? initialState,
);

/* ------------------ Entity Adapter Selectors ------------------ */

export const {
  selectAll: selectBulkSpares,
  selectById: selectBulkSpareById,
  selectIds: selectBulkSpareIds,
} = bulkSpareAdapter.getSelectors((state) => selectBulkSpareData(state));
